import { useCallback, useEffect, useState } from "react";
import { environment } from "environment/environment";

const ACCOUNT_NAME = "PK";
const UNDERLYING = "NIFTY";
const HISTORY_LIMIT = 100;
const ARCHIVE_REFRESH_GRACE_MS = 5 * 1000;
const MARKET_TIME_ZONE = "Asia/Kolkata";

const historyUrl = (expiryDate, sessionDate) => {
  const params = new URLSearchParams({
    accountName: ACCOUNT_NAME,
    underlying: UNDERLYING,
    limit: String(HISTORY_LIMIT),
  });
  if (expiryDate && expiryDate !== "--") params.set("expiryDate", expiryDate);
  if (sessionDate && /^\d{4}-\d{2}-\d{2}$/.test(sessionDate)) params.set("sessionDate", sessionDate);
  const separator = environment.angelOneGammaBlastPremiumMapHistoryUrl.includes("?") ? "&" : "?";
  return `${environment.angelOneGammaBlastPremiumMapHistoryUrl}${separator}${params.toString()}`;
};

const readJson = async (response) => {
  if (!response.ok) {
    throw new Error(`Gamma Blast premium-map history request failed (${response.status})`);
  }
  return response.json();
};

const delayUntilNextArchiveRefresh = (now = new Date()) => {
  const nextBoundary = new Date(now);
  const minutes = nextBoundary.getMinutes();
  nextBoundary.setMinutes(minutes - (minutes % 5) + 5, 0, 0);
  return Math.max(1000, nextBoundary.getTime() - now.getTime() + ARCHIVE_REFRESH_GRACE_MS);
};

const marketDate = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MARKET_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const values = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
  return `${values.year}-${values.month}-${values.day}`;
};

/**
 * Reads the immutable five-minute premium-map archive. Refreshes shortly
 * after every clock boundary (:00, :05, :10, ...), allowing the backend's
 * one-minute scheduler time to persist the completed bucket first.
 */
export default function useAngelOneGammaBlastPremiumMapHistory(expiryDate, sessionDate) {
  // A fresh page load must always query the current IST market date. The
  // fallback also protects callers that have not added the date prop yet.
  const requestedSessionDate = sessionDate && /^\d{4}-\d{2}-\d{2}$/.test(sessionDate)
    ? sessionDate
    : marketDate();
  const [state, setState] = useState({
    history: [],
    loading: true,
    error: null,
  });

  const load = useCallback(async (signal) => {
    try {
      const response = await fetch(historyUrl(expiryDate, requestedSessionDate), { signal });
      const value = await readJson(response);
      setState({
        history: Array.isArray(value) ? value : [],
        loading: false,
        error: null,
      });
    } catch (cause) {
      if (cause?.name === "AbortError") return;
      setState((current) => ({ ...current, loading: false, error: cause }));
    }
  }, [expiryDate, requestedSessionDate]);

  useEffect(() => {
    let disposed = false;
    let refreshTimeoutId;
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: null }));
    void load(controller.signal);

    const scheduleNextRefresh = () => {
      refreshTimeoutId = window.setTimeout(async () => {
        if (disposed) return;
        await load(undefined);
        if (!disposed) scheduleNextRefresh();
      }, delayUntilNextArchiveRefresh());
    };

    if (requestedSessionDate === marketDate()) scheduleNextRefresh();
    return () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(refreshTimeoutId);
    };
  }, [load, requestedSessionDate]);

  return { ...state, reload: () => load(undefined) };
}
